import { z } from "zod";

// ─── Schema ───────────────────────────────────────────────────────────────
//
// Onyx is a Saint Laurent / Haute Couture editorial layout. Every visible
// label, headline, marquee line and lookbook card is parametrized here.
// Texts may contain tokens (see `resolveTokens` below) — e.g. `{title}`,
// `{year}`, `{dayMonth}`, `{weekday}`, `{location}`.

const hexColor = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Geçersiz renk");

export const onyxLookCardSchema = z.object({
  num: z.string().min(1).max(40),
  // Empty until the organizer uploads a look image; the layout falls back to
  // gallery images (onyx/index.tsx) so an empty URL is a valid, saveable state.
  imageUrl: z.string().max(2048),
  imageKey: z.string().optional(),
  detail1: z.string().max(60),
  detail2: z.string().max(60),
  detail3: z.string().max(60),
});

/**
 * Tema metinlerindeki `{tarih}`/`{mekan}` token'ları hangi etkinlikten beslenir.
 *
 * Düğünde iki etkinlik varsa (kına + düğün) kayan şerit, hero ve RSVP metinleri
 * bir tanesini anmak zorunda. `auto` = **en yakın gelecekteki** etkinlik; kına
 * geçince metinler kendiliğinden düğüne döner, elle müdahale gerekmez.
 * Sabitlemek isteyen organizatör `primary`/`event2` seçer.
 */
export const onyxTokenEventSchema = z.enum(["auto", "primary", "event2"]);
export type OnyxTokenEvent = z.infer<typeof onyxTokenEventSchema>;

export const onyxConfigSchema = z.object({
  // Opsiyonel: eski kayıtlarda yok. Değeri `ONYX_DEFAULTS` üzerinden gelen
  // deep-merge dolduruyor (join.tsx), tüketiciler ayrıca `?? "auto"` diyor.
  // ⚠️ `.default()` DEĞİL: giriş tipini opsiyonel yapıp registry'nin
  // `z.ZodType<TConfig>` sözleşmesini kırıyor.
  tokenEvent: onyxTokenEventSchema.optional(),
  marquee: z.object({
    top: z.string().max(400),
    bottom: z.string().max(400),
  }),
  hero: z.object({
    collectionLabel: z.string().max(120),
    issueLabel: z.string().max(60),
    attire: z.string().max(120),
    /**
     * Hero'nun altındaki kaydırma ipucu etiketi. Opsiyonel — eski kayıtlarda
     * yok, değeri `ONYX_DEFAULTS` deep-merge'inden gelir. Boş bırakılırsa
     * yalnız animasyonlu nokta kalır (etiketsiz, daha sessiz bir varyant).
     */
    scrollCue: z.string().max(40).optional(),
  }),
  splitMoment: z.object({
    label: z.string().max(60),
    quote: z.string().max(280),
    imageIndex: z.number().int().min(0).max(20),
  }),
  lookbook: z.object({
    headerLabel: z.string().max(60),
    headerSeason: z.string().max(60),
    forewordLabel: z.string().max(60),
    forewordText: z.string().max(280),
    looks: z.array(onyxLookCardSchema).min(1).max(7),
    outroLabel: z.string().max(80),
    outroTitle: z.string().max(40),
    outroQuote: z.string().max(280),
    outroSmall: z.string().max(160),
    outroNavLeft: z.string().max(40),
    outroNavMiddle: z.string().max(40),
    outroNavRight: z.string().max(40),
  }),
  dateSection: z.object({
    label: z.string().max(60),
    leftLabel: z.string().max(40),
    rightLabel: z.string().max(40),
  }),
  story: z.object({
    label: z.string().max(60),
    headline: z.string().max(160),
    imageIndex: z.number().int().min(0).max(20),
  }),
  venue: z.object({
    label: z.string().max(60),
    imageIndex: z.number().int().min(0).max(20),
  }),
  rsvp: z.object({
    label: z.string().max(60),
    heading: z.string().max(80),
    closingPrefix: z.string().max(160),
  }),
  colors: z.object({
    accent: hexColor,
    background: hexColor,
  }),
});

export type OnyxLookCard = z.infer<typeof onyxLookCardSchema>;
export type OnyxConfig = z.infer<typeof onyxConfigSchema>;

// ─── Defaults (current hardcoded values from the demo layout) ──────────────

export const ONYX_DEFAULTS: OnyxConfig = {
  // Metinler TÜRKÇE ama editoryal üslup korunuyor (tema kimliği dergi dili
  // üzerine kurulu): "Lookbook"→"Albüm", "Foreword"→"Önsöz", "Fin"→"Son",
  // "Vol. I"→"Cilt I". Organizatör tema editöründen hepsini değiştirebilir.
  tokenEvent: "auto",
  marquee: {
    top: "{titleUpper} ● {eventNameUpper} ● {dayMonth}.{year} ● {locationUpper} ●",
    bottom: "ŞIK GİYİM ● KATILIM BİLDİRİMİ RİCA OLUNUR ● {weekdayUpper} AKŞAMI ●",
  },
  hero: {
    collectionLabel: "Bir Düğün Albümü",
    issueLabel: "№ {year}",
    attire: "Şık giyim · {rsvpBy} tarihine kadar bildiriniz",
    scrollCue: "Kaydırın",
  },
  splitMoment: {
    label: "Tek Perde",
    quote: "Bir görüş, bir karar, bir ömür.",
    imageIndex: 0,
  },
  lookbook: {
    headerLabel: "— Albüm —",
    headerSeason: "{year} Yazı",
    forewordLabel: "— Önsöz —",
    forewordText: "Beş bölüm. Bir gece. Sayısız hatıra.",
    looks: [
      { num: "BÖLÜM 01", imageUrl: "", detail1: "Tören", detail2: "Beyaz gelinlik", detail3: "18:00" },
      { num: "BÖLÜM 02", imageUrl: "", detail1: "Kokteyl", detail2: "İkramlar", detail3: "20:00" },
      { num: "BÖLÜM 03", imageUrl: "", detail1: "Yemek", detail2: "Şık giyim", detail3: "21:30" },
      { num: "BÖLÜM 04", imageUrl: "", detail1: "Karşılama", detail2: "Canlı müzik", detail3: "23:00" },
      { num: "BÖLÜM 05", imageUrl: "", detail1: "Devamı", detail2: "DJ", detail3: "01:00" },
    ],
    outroLabel: "● Albümün Sonu ●",
    outroTitle: "— Son —",
    outroQuote: "Beş bölüm. Bir gece.\nSayısız hatıra.",
    outroSmall: "Sıradaki bölüm sizinle başlıyor.",
    outroNavLeft: "Cilt I",
    outroNavMiddle: "Sayfa I — V",
    outroNavRight: "Devam ↓",
  },
  dateSection: {
    label: "● Tarih ●",
    leftLabel: "Tarih",
    rightLabel: "Mekan",
  },
  story: {
    label: "● Hikayemiz ●",
    headline: "İki şehir,\nbir hikaye.",
    imageIndex: 2,
  },
  venue: {
    label: "● Mekan ●",
    imageIndex: 4,
  },
  rsvp: {
    label: "● KATILIM ●",
    heading: "Onayınız",
    closingPrefix: "Şık giyim · {rsvpBy} tarihine kadar bildiriniz",
  },
  colors: {
    accent: "#dc2626",
    background: "#0a0a0a",
  },
};

// ─── Token substitution ────────────────────────────────────────────────────

export interface TokenContext {
  title: string;
  titleUpper: string;
  titleFirst: string;
  titleSecond: string;
  year: string;
  day: string;
  month: string;
  monthName: string;
  monthShort: string;
  dayMonth: string;
  location: string;
  locationUpper: string;
  locationCity: string;
  weekday: string;
  weekdayUpper: string;
  time: string;
  rsvpBy: string;
  /** Token'ları besleyen etkinliğin adı ("Kına" / "Düğün"). Tek etkinlikte boş. */
  eventName: string;
  eventNameUpper: string;
}

export function makeTokenContext(args: {
  title: string;
  dateTime: string | null;
  location: string | null;
  locale?: string;
  /** İki etkinlikli düğünde token'ları besleyen etkinliğin adı. */
  eventName?: string;
}): TokenContext {
  const { title, dateTime, location, locale = "tr-TR", eventName = "" } = args;
  const d = dateTime ? new Date(dateTime) : null;
  const valid = d && !Number.isNaN(d.getTime());

  const pad2 = (n: number) => n.toString().padStart(2, "0");

  const year = valid ? d!.getFullYear().toString() : "—";
  const day = valid ? pad2(d!.getDate()) : "—";
  const month = valid ? pad2(d!.getMonth() + 1) : "—";
  const monthName = valid
    ? d!.toLocaleDateString(locale, { month: "long" })
    : "—";
  const monthShort = valid
    ? d!.toLocaleDateString(locale, { month: "short" })
    : "—";
  const dayMonth = valid ? `${day}.${month}` : "—";
  const weekday = valid
    ? d!.toLocaleDateString(locale, { weekday: "long" })
    : "—";
  const time = valid
    ? d!.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
    : "—";
  // RSVP by: 7 days before the wedding, formatted "DD MMMM"
  const rsvpBy = valid
    ? new Date(d!.getTime() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
      })
    : "—";

  const titleParts = (title || "").split("&").map((s) => s.trim());
  const loc = location ?? "";

  return {
    title: title || "",
    titleUpper: (title || "").toUpperCase(),
    titleFirst: titleParts[0] ?? title ?? "",
    titleSecond: titleParts[1] ?? "",
    year,
    day,
    month,
    monthName,
    monthShort,
    dayMonth,
    location: loc,
    locationUpper: loc.toUpperCase(),
    locationCity: loc.split(",")[0] ?? loc,
    weekday,
    weekdayUpper: weekday.toUpperCase(),
    time,
    rsvpBy,
    eventName,
    eventNameUpper: eventName.toUpperCase(),
  };
}

const TOKEN_RE = /\{(\w+)\}/g;

export function resolveTokens(template: string, ctx: TokenContext): string {
  if (!template) return "";
  return template.replace(TOKEN_RE, (_, key) => {
    if (key in ctx) return (ctx as unknown as Record<string, string>)[key] ?? "";
    return `{${key}}`;
  });
}

/** Tokens organizer editor exposes as auto-complete hints. */
export const ONYX_AVAILABLE_TOKENS: { token: string; label: string }[] = [
  { token: "{title}", label: "Düğün başlığı" },
  { token: "{titleUpper}", label: "Başlık (büyük harf)" },
  { token: "{titleFirst}", label: "Başlığın ilk kısmı (& öncesi)" },
  { token: "{titleSecond}", label: "Başlığın ikinci kısmı (& sonrası)" },
  { token: "{year}", label: "Yıl" },
  { token: "{day}", label: "Gün (01-31)" },
  { token: "{month}", label: "Ay (01-12)" },
  { token: "{monthName}", label: "Ay adı (Ağustos)" },
  { token: "{monthShort}", label: "Ay kısa (Ağu)" },
  { token: "{dayMonth}", label: "DD.MM" },
  { token: "{location}", label: "Mekan" },
  { token: "{locationUpper}", label: "Mekan (büyük harf)" },
  { token: "{locationCity}", label: "Şehir (ilk parça)" },
  { token: "{weekday}", label: "Haftanın günü" },
  { token: "{weekdayUpper}", label: "Haftanın günü (büyük harf)" },
  { token: "{time}", label: "Saat (HH:MM)" },
  { token: "{rsvpBy}", label: "RSVP son tarih (-7 gün)" },
  { token: "{eventName}", label: "Etkinlik adı (Kına / Düğün)" },
  { token: "{eventNameUpper}", label: "Etkinlik adı (büyük harf)" },
];
